import styles from "./NewsSearchBar.module.css";
import SearchSVG from "../SVGs/SearchSVG";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { PostContext } from "../PostProvider/PostProvider";
import NewsSearchBarResult from "../NewsSearchBarResult/NewsSearchBarResult";
import useDetectKeelMind from "../../hooks/useDetectKeelMind";
import { useActiveElement } from "../../hooks/useActiveElement";
import { useNavigate } from "react-router-dom";
function NewsSearchBar({
    searchBarState,
    setSearchBarState,
    placeholderText = "Search for a post or category...",
    initialValue = "",
    iconButton = false,
}) {
    const { searchableData } = useContext(PostContext);
    const isKeelMind = useDetectKeelMind(true);
    const inputRef = useRef(null);
    const resultsContainerRef = useRef(null);
    const [searchValue, setSearchValue] = useState(initialValue);
    const activeElement = useActiveElement();
    const isResultFocused = useMemo(
        () => activeElement.className.includes("NewsSearchBarResult"),
        [activeElement]
    );
    const navigate = useNavigate();

    const filteredSearchResults = useMemo(
        () =>
            searchableData?.filter((v) => {
                return (
                    `${v.name}`
                        .toLowerCase()
                        .includes(searchValue.toLowerCase()) &&
                    searchValue !== ""
                );
            }),
        [searchableData, searchValue]
    );

    function handleInputChange(e) {
        setSearchValue(e.target.value);
    }

    function handleSearchButtonClick() {
        setSearchBarState(true);
    }

    function handleKeyDown(e) {
        if (e.keyCode === 27) {
            // esc
            e.preventDefault();
            setSearchBarState(false);
        } else if (e.keyCode === 13) {
            // enter
            if (!isResultFocused && searchValue.length !== 0) {
                navigate(
                    isKeelMind
                        ? "/news/search?q=" + searchValue
                        : "/mind/news/search?q=" + searchValue
                );
            }
        } else if (e.keyCode === 32) {
            // space
            if (isResultFocused) {
                e.preventDefault();
                e.target.click();
            }
        } else if (e.keyCode === 40) {
            // down
            e.preventDefault();
            if (resultsContainerRef.current?.children.length > 0) {
                if (
                    isResultFocused &&
                    document.activeElement.nextElementSibling
                ) {
                    document.activeElement.nextElementSibling.focus();
                } else if (activeElement === inputRef.current) {
                    resultsContainerRef.current.children[0].focus();
                } else {
                    inputRef.current.focus();
                }
            }
        } else if (e.keyCode === 38) {
            // up
            e.preventDefault();
            if (resultsContainerRef.current?.children.length > 0) {
                if (
                    isResultFocused &&
                    document.activeElement.previousElementSibling
                ) {
                    document.activeElement.previousElementSibling.focus();
                } else if (activeElement === inputRef.current) {
                    [...resultsContainerRef.current.children].at(-1).focus();
                } else {
                    inputRef.current.focus();
                }
            }
        }
    }

    useEffect(() => {
        if (searchBarState) {
            inputRef.current.focus();
        }
    }, [searchBarState]);

    return (
        <div
            id={styles.search_container}
            onKeyDown={handleKeyDown}
            className={iconButton ? styles.icon_button : ""}
        >
            <button
                id={styles.search_bar_button}
                onClick={handleSearchButtonClick}
            >
                <SearchSVG style={{ fill: "#6C7588" }} width={20} />
                {iconButton ? null : (
                    <span>
                        {searchValue.length > 0 ? searchValue : placeholderText}
                    </span>
                )}
            </button>
            <div
                id={styles.search_overlay}
                className={searchBarState ? "" : styles.hide}
            >
                <div id={styles.top}>
                    <SearchSVG width={20} />
                    <input
                        autoComplete="off"
                        ref={inputRef}
                        value={searchValue}
                        type="text"
                        name="search"
                        id={styles.search_input}
                        placeholder={placeholderText}
                        onChange={handleInputChange}
                    />
                </div>
                {searchValue.length !== 0 && (
                    <>
                        <hr className={styles.divider} />
                        {filteredSearchResults.length > 0 && (
                            <div id={styles.message_bar}>
                                <div id={styles.results_count}>
                                    Found {filteredSearchResults.length} results
                                    for{" "}
                                    <span
                                        style={{ color: "var(--keel-orange)" }}
                                    >
                                        {searchValue}
                                    </span>
                                </div>
                                <div id={styles.enter_message}>
                                    Press Enter to view{" "}
                                    {isResultFocused
                                        ? "selection"
                                        : "all results"}
                                </div>
                            </div>
                        )}
                    </>
                )}
                {filteredSearchResults.length !== 0 && (
                    <div
                        id={styles.results_container}
                        ref={resultsContainerRef}
                    >
                        {filteredSearchResults.map((result) => (
                            <NewsSearchBarResult
                                result={result}
                                key={result.slug}
                            />
                        ))}
                    </div>
                )}
                {filteredSearchResults.length === 0 &&
                    searchValue.length !== 0 && (
                        <div id={styles.no_results_container}>
                            <img
                                src="/images/no-search-results-icon.png"
                                alt="No search results"
                                draggable={false}
                            />
                            <div className={styles.message}>
                                <div>
                                    No results found for{" "}
                                    <span
                                        style={{
                                            color: "var(--keel-orange)",
                                        }}
                                    >
                                        {searchValue}
                                    </span>
                                </div>
                            </div>
                        </div>
                    )}
            </div>
        </div>
    );
}

export default NewsSearchBar;
